import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

/**
 * AuthGuard
 *
 * Rule: NEVER redirect to /login just because the access token is expired.
 *       Always attempt a silent token refresh first.
 *       Only redirect to /login when:
 *         - There is no refresh token stored at all, OR
 *         - The refresh API call itself fails (token revoked/expired on server)
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(private authService: AuthService) {}

  canActivate(): Observable<boolean> | boolean {

    // ── Fast path: access token is still valid ────────────────────────────
    if (this.authService.isAuthenticated()) {
      return true;
    }

    // ── Access token expired or missing — always try refresh first ─────────
    // Even if there is no refresh token, we attempt the call and let the
    // server respond. If the call fails for any reason, clearSessionAndRedirect
    // handles the login redirect.
    return this.authService.refreshAccessToken().pipe(

      map(response => {
        if (response.success && response.data?.accessToken) {
          // Refresh succeeded — storeSession() was already called inside
          // refreshAccessToken(). Allow navigation, user stays on their page.
          return true;
        }
        // Server returned 200 but success=false (refresh token revoked etc.)
        this.authService.clearSessionAndRedirect();
        return false;
      }),

      catchError(() => {
        // Network error, 401, 400 — refresh token invalid or absent on server.
        this.authService.clearSessionAndRedirect();
        return of(false);
      })
    );
  }
}
